import {
  Box,
  ButtonIcon,
  Icon,
  MenuOption,
  Typography,
} from "@saviynt/design-system";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import PoliciesFindingsList from "./PoliciesFindingsList/PoliciesFindingsList";
import {
  PermissionProvider,
  usePermissionContext,
} from "../../context/permissionContext";
import useUserPermissions, {
  PERMISSION_KEYS,
} from "../../hooks/useUserPermissions";
import ActionResultBanner from "./ActionResultBanner/ActionResultBanner";
import { TableSkeletonLoader } from "@repo/ui";
import { AlertProvider } from "./context/AlertContext";
import { useRouter, useSearch } from "@tanstack/react-router";
import {
  useFindingsCommon,
  useFindingsPayload,
} from "./hooks/useFindingsCommon";
import { FindingsDataProvider } from "./context/FindingsDataContext";
import { SelectionProvider } from "./context/SelectionContext";
import { usePolicesFindingsFilters } from "../../hooks/usePolicyFindings";
import DetailedFindigsStickyFooter from "./DetailedFindigsStickyFooter/DetailedFindigsStickyFooter";
import FindingsFilterControls from "../PoliciesFindingsFilter/PoliciesFindingsFilterControls";
import FindingsFilterOptions from "../PoliciesFindingsFilter/PoliciesFindingsFilterOptions";
import { usePoliciesFindingsExport } from "./hooks/usePoliciesFindingsExport";
import "./PoliciesFindingsPage.css";

function PolicyFindingsContent() {
  const intl = useIntl();
  const router = useRouter();
  const [isMounted, setIsMounted] = useState(false);
  const {
    name,
    status: searchStatus,
    category,
  } = useSearch({
    from: "/_policies/findings",
  });
  const policyName = name ?? "";
  const status = searchStatus ?? "";
  const { setIsPermissionEnabled } = usePermissionContext();
  const {
    isFilterOpen,
    searchValue,
    filterOptions,
    debouncedSearch,
    debouncedFilters,
    hasSelectedFilters,
    handleFilterToggle,
    setSearchValue,
    setFilterOptions,
    handleClear,
  } = useFindingsCommon();

  const { data, isLoading, initializedFilterOptions, hasInitializedOptions } =
    usePolicesFindingsFilters({
      policyName,
      status,
    });

  const { permission: remediationPermission } = useUserPermissions(
    PERMISSION_KEYS.ISPM_FINDINGS_MANAGE,
  );
  const { permission: assetOwnership } = useUserPermissions(
    PERMISSION_KEYS.ISPM_FINDINGS_MANAGE,
  );

  useEffect(() => {
    setIsPermissionEnabled(assetOwnership?.enabled ?? false);
  }, [assetOwnership, setIsPermissionEnabled]);

  useEffect(() => {
    setIsPermissionEnabled(assetOwnership?.enabled ?? false);
  }, [assetOwnership, setIsPermissionEnabled]);

  useEffect(() => {
    if (hasInitializedOptions) {
      setFilterOptions((prev) => ({
        ...prev,
        ...(initializedFilterOptions as Record<string, MenuOption[]>),
      }));
    }
  }, [hasInitializedOptions, initializedFilterOptions, setFilterOptions]);

  const basePayload = useFindingsPayload(debouncedSearch, debouncedFilters);
  const [payload, setPayload] = useState(basePayload);

  useEffect(() => {
    setPayload(basePayload);
  }, [basePayload]);

  const { handleExport } = usePoliciesFindingsExport(payload);

  const handleClearWithFlag = useCallback(() => {
    handleClear();
  }, [handleClear]);

  const shouldShowTable = useMemo(() => {
    if (!hasInitializedOptions) return false;
    if (!policyName && !status) return hasInitializedOptions;

    const policyNames = (payload as Record<string, unknown>).policy_name as
      | string[]
      | undefined;
    const statuses = (payload as Record<string, unknown>).status as
      | string[]
      | undefined;

    const isFilterValuesSet =
      (policyName &&
        policyNames?.some(
          (v) => v.toLowerCase() === policyName.toLowerCase(),
        )) ||
      (status &&
        statuses?.some((v) => v.toLowerCase() === status.toLowerCase()));

    if (!isMounted && isFilterValuesSet) {
      setIsMounted(true);
    }

    return isMounted || !!isFilterValuesSet;
  }, [hasInitializedOptions, policyName, status, payload, isMounted]);

  return (
    <>
      <ActionResultBanner />
      <div className="w-full">
        <header className="PoliciesFindingsList-header">
          <ButtonIcon
            kind="ghost"
            icon={<Icon kind="ChevronLeft" size="medium" />}
            ariaLabel={intl.formatMessage({
              defaultMessage: "Back button",
              id: "6IE8cX",
            })}
            onClick={() => router.history.back()}
          />
          <Typography kind="h1" desktop="h1-d" mobile="h1-d">
            {intl.formatMessage({
              defaultMessage: "Findings",
              description: "Page title for the detailed findings view",
              id: "vnQfqr",
            })}
          </Typography>
        </header>

        <div className="px-12 py-4">
          <FindingsFilterControls
            isFilterOpen={isFilterOpen}
            handleFilterClick={handleFilterToggle}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            hasSelectedFilters={hasSelectedFilters}
            handleExportOnClick={handleExport}
          />
          {isFilterOpen && (
            <FindingsFilterOptions
              filterOptions={filterOptions}
              setFilterOptions={setFilterOptions}
              onClear={handleClearWithFlag}
              data={data}
              isLoading={isLoading}
            />
          )}
        </div>
        {shouldShowTable ? (
          <PoliciesFindingsList
            payload={payload}
            onPayloadChange={setPayload}
            remediationPermission={remediationPermission?.enabled ?? false}
            category={category}
          />
        ) : (
          <Box className="px-12">
            <TableSkeletonLoader rows={10} />
          </Box>
        )}
        {remediationPermission?.enabled && (
          <DetailedFindigsStickyFooter payload={payload} />
        )}
      </div>
    </>
  );
}

export default function PoliciesFindingsPage() {
  return (
    <PermissionProvider>
      <AlertProvider>
        <SelectionProvider>
          <FindingsDataProvider>
            <PolicyFindingsContent />
          </FindingsDataProvider>
        </SelectionProvider>
      </AlertProvider>
    </PermissionProvider>
  );
}
